import { ReactNode, Dispatch, SetStateAction, ReactElement, useCallback } from 'react';
import { Form, FormProps, Submit, useSubmitHandler, SubmitHandlerOptions } from '@ff-it/form';
import { HTTPMethod } from '@ff-it/api';
import { Button, Stack } from '@ff-it/ui';
import { useModel, useEntity } from './context';
import { Perm, useHasPerms } from 'core/permissions';
import { PermissionDenied } from './errors';
import { toast } from 'react-toastify';
import { Page, Heading, Breadcrumb } from '../layout';
import cx from 'clsx';
import { Link } from 'react-router-dom';

interface UpdateSceneProps<T> extends Omit<UpdateContainerProps<T>, 'initialValues' | 'method' | 'url' | 'updateItem'> {
  perm?: Perm;
  canCreate?: Perm;
  createPath?: string | null;
  canDelete?: Perm;
  deletePath?: string | null;
  method?: HTTPMethod;
  viewDisabled?: boolean;
  copy?: (item: T) => any;
  mapInitialValues?: (item: T) => any;
}

export function UpdateScene<T = any>(props: UpdateSceneProps<T>): ReactElement {
  const { permissions, title } = useModel();
  const { item, setItem, endpoint: url } = useEntity<T>();
  const {
    perm,
    children,
    canDelete,
    deletePath = 'delete',
    canCreate,
    createPath = '../create',
    heading = <Heading title={`Update ${title}`}></Heading>,
    actions,
    className,
    viewDisabled,
    disabled,
    copy,
    mapInitialValues,
    ...formProps
  } = props;

  const [hasView, hasUpdate, hasDelete, hasCreate] = useHasPerms(
    permissions?.view,
    perm || permissions?.change,
    canDelete || permissions?.delete,
    canCreate || permissions?.add,
  );

  if (!hasUpdate && !(viewDisabled && hasView)) {
    return <PermissionDenied />;
  }

  return (
    <UpdateContainer
      {...formProps}
      disabled={disabled || !hasUpdate}
      className={className}
      heading={heading}
      initialValues={mapInitialValues ? mapInitialValues(item) : item}
      updateItem={setItem}
      url={url}
      actions={
        <>
          {hasDelete && deletePath && (
            <Button
              variant="secondary-danger"
              size="sm"
              testId="delete"
              disabled={formProps.disabled}
              icon="xmark"
              asChild
            >
              <Link to={deletePath}>Delete</Link>
            </Button>
          )}
          {hasCreate && createPath && (
            <Button variant="secondary" size="sm" testId="create" icon="circle-plus" asChild>
              <Link to={createPath}>Create</Link>
            </Button>
          )}
          {hasCreate && createPath && copy && (
            <Button variant="secondary" size="sm" testId="copy" icon="copy" asChild>
              <Link to={createPath} state={{ initialValues: copy(item), copyFromId: (item as any).id }}>
                Copy
              </Link>
            </Button>
          )}
          {actions}
        </>
      }
    >
      {children}
    </UpdateContainer>
  );
}

interface UpdateContainerProps<T> extends Omit<FormProps<any>, 'onSubmit'>, SubmitHandlerOptions<any, T> {
  className?: string;
  heading?: ReactNode | null;
  breadcrumb?: ReactNode | null;
  actions?: ReactNode;
  successMessage?: ReactNode;
  method?: HTTPMethod;
  url: string;
  updateItem: Dispatch<SetStateAction<T>>;
}

export function UpdateContainer<T>({
  className,
  heading,
  breadcrumb = 'Update',
  actions,
  children,
  mapValues,
  successMessage = 'Item has been updated.',
  method = 'PUT',
  url,
  updateItem,
  ...formProps
}: UpdateContainerProps<T>): ReactElement {
  // biome-ignore lint/correctness/useExhaustiveDependencies: update item should be stable
  const onSuccess = useCallback(
    (result: any): void => {
      toast.success(successMessage, { toastId: 'update-scene-toast' });
      if (method === 'PATCH') {
        updateItem((item) => ({ ...item, ...result }));
      } else {
        updateItem(result);
      }
    },
    [successMessage, method],
  );

  const onSubmit = useSubmitHandler<any, T>({ url, method }, { mapValues, onSuccess });

  return (
    <Page className={cx('scene scene-update', className)}>
      {breadcrumb && <Breadcrumb to={null}>{breadcrumb}</Breadcrumb>}
      {heading}
      <Form noValidate autoComplete="off" {...formProps} onSubmit={onSubmit}>
        {children}
        <div className="container px-0 ml-0">
          <Stack direction="horizontal" mb="3">
            <Submit data-test-id="update">Update</Submit>
            {actions}
          </Stack>
        </div>
      </Form>
    </Page>
  );
}
